<template>
  <DDialog v-model:visible="visible" modal class="w-full max-w-md" header="Create New Project" :draggable="false">
    <div class="flex w-full flex-col">
      <label class="mb-2 block font-medium text-gray-300"> File Name </label>

      <DInputText v-model="newFileName" placeholder="Enter workspace name" class="w-full" />
    </div>

    <template #footer>
      <div style="margin-top: 16px; display: flex; justify-content: flex-end; gap: 8px">
        <DButton @click="$emit('close')" type="secondary">Cancel</DButton>
        <DButton severity="primary" @click.stop="create" @keyup.enter="create" :loading="loading" :disabled="loading"
          >Create</DButton
        >
      </div>
    </template>
  </DDialog>
</template>

<script setup>
import { useCruxStore } from '@/stores/api/crux';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useFolderStore } from '@/stores/api/folder';
import { useSpaceStore } from '@/stores/api/space';
import { useWorkspaceStore } from '@/stores/api/workspace';
import { DButton, DDialog, DInputText } from '@disguise/cloud-ui';
import { onMounted, ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const cruxStore = useCruxStore();
const cruxModuleStore = useCruxModuleStore();
const folderStore = useFolderStore();
const spaceStore = useSpaceStore();
const workspaceStore = useWorkspaceStore();

const currentWorkspace = ref(localStorage.getItem('crux-selected-workspace-id') || null);
const newFileName = ref('New File');
const selectedTemplate = ref(null);
const currentSpace = ref(null);
const currentFolder = ref(null);
const loading = ref(false);
const visible = ref(true);

const emit = defineEmits(['close']);

// Template data
const myTemplates = ref([
  {
    id: 'blank',
    name: 'Blank Scene',
    description: 'Start with an empty scene',
    icon: 'pi pi-plus',
    isBlank: true,
  },
]);

const loadWorkspaces = async () => {
  await workspaceStore.fetch();
};

const loadSpaces = async () => {
  if (currentWorkspace.value) {
    await spaceStore
      .fetchForWorkspace(currentWorkspace.value)
      .then((response) => {
        spaceStore.currentSpace = spaceStore.spaces[0]?.code || null;
        currentSpace.value = spaceStore.currentSpace;
      })
      .catch((error) => {
        spaceStore.currentSpace = null;
        currentSpace.value = null;
        console.error('Error loading spaces:', error);
      });
  }
};
// Persist selected workspace to localStorage (shared with ListFiles)
watchEffect(() => {
  if (currentWorkspace.value) {
    localStorage.setItem('crux-selected-workspace-id', currentWorkspace.value);

    loadSpaces();
  } else {
    localStorage.removeItem('crux-selected-workspace-id');
  }

  if (currentSpace.value) {
    // load folder
    folderStore
      .fetchForSpace(currentSpace.value)
      .then((response) => {
        folderStore.currentFolder = folderStore.folders.find((folder) => {
          return folder.name === 'Root';
        })?.code;
        currentFolder.value = folderStore.currentFolder;
      })
      .catch((error) => {
        folderStore.currentFolder = null;
        currentFolder.value = null;
        console.error('Error loading folders:', error);
      });
  }

  if (!visible.value) {
    emit('close');
  }
});

onMounted(async () => {
  await loadWorkspaces().then((response) => {
    loadSpaces();
  });

  // Auto-select blank template by default
  selectedTemplate.value = myTemplates.value.find((t) => t.id === 'blank');
});

const create = async () => {
  if (currentFolder.value) {
    loading.value = true;
    const asset = await cruxStore.store({
      name: newFileName.value || 'New File',
      folder: currentFolder.value,
    });

    if (asset && asset.asset) {
      // create default modules
      await cruxModuleStore.createDefaultModulesForAsset(asset.code);

      visible.value = false;
      loading.value = false;
      // Navigate to the editor with the created asset
      router.push(`/editor/${asset.asset}`);
    }
  }
};
</script>
